import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Loader2, Search } from 'lucide-react';
import {
  ComplianceService,
  ComplianceServiceCreate,
  ComplianceServiceUpdate,
  COMPLIANCE_CATEGORIES,
  RETURN_FREQUENCIES,
  TASK_PRIORITIES,
} from '@/app/types/compliance';
import { searchItemsAsync, getAllItemsAsync, EnrichedItem } from '@/lib/indexeddb/database';
import { listTeams } from '@/app/services/teamService';
import { useSQLiteSync } from '@/hooks/useSQLiteSync';
import { useDebounce } from '@/hooks/useDebounce';
import { useAuthStore } from '@/app/store/authStore';
import { Separator } from '@/components/ui/separator';

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  code: z
    .string()
    .min(1, 'Code is required')
    .max(20, 'Code too long')
    .regex(/^[A-Z0-9_-]+$/, 'Code must be uppercase letters, numbers, underscores, or hyphens'),
  description: z.string().max(500, 'Description too long').optional().nullable(),
  category: z.enum(['indirect_tax', 'direct_tax', 'compliance'], {
    required_error: 'Please select a category',
  }),
  portal_url: z.string().url('Invalid URL').optional().nullable().or(z.literal('')),
  is_active: z.boolean().default(true),
  sort_order: z.coerce.number().int().min(0).default(0),
  item_id: z.coerce.number().optional().nullable(),
  // Scheduling fields
  frequency: z.string().optional().nullable(),
  due_day_of_month: z.coerce.number().int().min(1).max(31).optional().nullable(),
  due_days_after_period: z.coerce.number().int().min(0).optional().nullable(),
  applicable_months: z.string().optional().nullable(),
  period_format: z.string().optional().nullable(),
  // Generation config
  generate_days_before_due: z.coerce.number().int().min(0).default(7),
  auto_assign_to_subscription_user: z.boolean().default(true),
  default_team_id: z.coerce.number().optional().nullable(),
  task_title_template: z.string().max(200).optional().nullable(),
  default_priority: z.string().default('medium'),
});

type FormValues = z.infer<typeof formSchema>;

interface ComplianceServiceFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: ComplianceServiceCreate | ComplianceServiceUpdate) => void;
  defaultValues?: ComplianceService | null;
  isLoading: boolean;
  mode: 'create' | 'edit';
}

const ComplianceServiceForm: React.FC<ComplianceServiceFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading,
  mode,
}) => {
  const userOrgId = useAuthStore((state) => state.userOrgId);
  const { isInitialized: isIndexedDBInitialized } = useSQLiteSync();

  // Item search state
  const [itemSearchOpen, setItemSearchOpen] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<EnrichedItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedItemName, setSelectedItemName] = useState<string>('');
  const debouncedSearchQuery = useDebounce(itemSearchQuery, 200);

  // Teams for default-team assignment
  const { data: teamsResponse } = useQuery({
    queryKey: ['teams-list'],
    queryFn: () => listTeams({ limit: 100 }),
    enabled: open,
  });
  const teams = teamsResponse?.items ?? [];

  // Load initial items when popover opens
  useEffect(() => {
    if (itemSearchOpen && isIndexedDBInitialized && userOrgId && !itemSearchQuery) {
      const loadInitialItems = async () => {
        setIsSearching(true);
        try {
          const allItems = await getAllItemsAsync(userOrgId);
          const initialItems = allItems.filter(item => item.is_active).slice(0, 15);
          setSearchResults(initialItems);
        } catch (error) {
          console.error('[ComplianceServiceForm] Error loading initial items:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      loadInitialItems();
    }
  }, [itemSearchOpen, isIndexedDBInitialized, userOrgId, itemSearchQuery]);

  // Search items when query changes
  useEffect(() => {
    if (debouncedSearchQuery.length > 1 && isIndexedDBInitialized && userOrgId) {
      const performSearch = async () => {
        setIsSearching(true);
        try {
          const results = await searchItemsAsync(debouncedSearchQuery, userOrgId, 50);
          setSearchResults(results);
        } catch (error) {
          console.error('[ComplianceServiceForm] Item search error:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      performSearch();
    } else if (debouncedSearchQuery.length === 0 && itemSearchOpen && isIndexedDBInitialized && userOrgId) {
      const loadInitialItems = async () => {
        setIsSearching(true);
        try {
          const allItems = await getAllItemsAsync(userOrgId);
          const initialItems = allItems.filter(item => item.is_active).slice(0, 15);
          setSearchResults(initialItems);
        } catch (error) {
          console.error('[ComplianceServiceForm] Error loading initial items:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      };
      loadInitialItems();
    }
  }, [debouncedSearchQuery, isIndexedDBInitialized, userOrgId, itemSearchOpen]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      code: '',
      description: '',
      category: 'indirect_tax',
      portal_url: '',
      is_active: true,
      sort_order: 0,
      item_id: null,
      frequency: null,
      due_day_of_month: null,
      due_days_after_period: null,
      applicable_months: '',
      period_format: '',
      generate_days_before_due: 7,
      auto_assign_to_subscription_user: true,
      default_team_id: null,
      task_title_template: '',
      default_priority: 'medium',
    },
  });

  // Reset form when dialog opens/closes or defaultValues change
  useEffect(() => {
    if (open) {
      if (defaultValues) {
        form.reset({
          name: defaultValues.name,
          code: defaultValues.code,
          description: defaultValues.description || '',
          category: defaultValues.category,
          portal_url: defaultValues.portal_url || '',
          is_active: defaultValues.is_active,
          sort_order: defaultValues.sort_order,
          item_id: defaultValues.item_id,
          frequency: defaultValues.frequency || null,
          due_day_of_month: defaultValues.due_day_of_month,
          due_days_after_period: defaultValues.due_days_after_period,
          applicable_months: defaultValues.applicable_months?.join(', ') || '',
          period_format: defaultValues.period_format || '',
          generate_days_before_due: defaultValues.generate_days_before_due ?? 7,
          auto_assign_to_subscription_user: defaultValues.auto_assign_to_subscription_user ?? true,
          default_team_id: defaultValues.default_team_id ?? null,
          task_title_template: defaultValues.task_title_template || '',
          default_priority: defaultValues.default_priority || 'medium',
        });
        setSelectedItemName(defaultValues.item_name || '');
      } else {
        form.reset({
          name: '',
          code: '',
          description: '',
          category: 'indirect_tax',
          portal_url: '',
          is_active: true,
          sort_order: 0,
          item_id: null,
          frequency: null,
          due_day_of_month: null,
          due_days_after_period: null,
          applicable_months: '',
          period_format: '',
          generate_days_before_due: 7,
          auto_assign_to_subscription_user: true,
          default_team_id: null,
          task_title_template: '',
          default_priority: 'medium',
        });
        setSelectedItemName('');
      }
    }
  }, [open, defaultValues, form]);

  const handleSubmit = (values: FormValues) => {
    // Parse applicable_months from comma-separated string to number array
    let applicableMonths: number[] | null = null;
    if (values.applicable_months) {
      const parsed = values.applicable_months
        .split(',')
        .map((s) => parseInt(s.trim()))
        .filter((n) => !isNaN(n) && n >= 1 && n <= 12);
      applicableMonths = parsed.length > 0 ? parsed : null;
    }

    const data: any = {
      name: values.name,
      code: values.code,
      description: values.description || null,
      category: values.category,
      portal_url: values.portal_url || null,
      is_active: values.is_active,
      sort_order: values.sort_order,
      item_id: values.item_id || null,
      frequency: values.frequency || null,
      due_day_of_month: values.due_day_of_month || null,
      due_days_after_period: values.due_days_after_period || null,
      applicable_months: applicableMonths,
      period_format: values.period_format || null,
      generate_days_before_due: values.generate_days_before_due ?? 7,
      auto_assign_to_subscription_user: values.auto_assign_to_subscription_user,
      default_team_id: values.default_team_id || null,
      task_title_template: values.task_title_template || null,
      default_priority: values.default_priority || 'medium',
    };
    onSubmit(data);
  };

  const isCodeDisabled = mode === 'edit';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Add Compliance Service' : 'Edit Compliance Service'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Create a new compliance service template with scheduling and task generation config.'
              : 'Update the compliance service details.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="GST"
                        {...field}
                        disabled={isCodeDisabled}
                        className="uppercase"
                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sort_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort Order</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Goods and Services Tax" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COMPLIANCE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Item Selector */}
            <FormField
              control={form.control}
              name="item_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Linked Service Item</FormLabel>
                  <Popover open={itemSearchOpen} onOpenChange={setItemSearchOpen}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={itemSearchOpen}
                          className="w-full justify-between font-normal"
                        >
                          {selectedItemName || 'Search and select an item...'}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[450px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Type item name or code..."
                          value={itemSearchQuery}
                          onValueChange={setItemSearchQuery}
                        />
                        <CommandList className="max-h-[250px]">
                          {isSearching && (
                            <div className="p-4 text-center">
                              <Loader2 className="h-4 w-4 animate-spin inline-block" />
                            </div>
                          )}
                          {!isSearching && searchResults.length === 0 && (
                            <CommandEmpty>No items found.</CommandEmpty>
                          )}
                          <CommandGroup>
                            <CommandItem
                              value="clear-item"
                              onSelect={() => {
                                field.onChange(null);
                                setSelectedItemName('');
                                setItemSearchOpen(false);
                                setItemSearchQuery('');
                              }}
                              className="cursor-pointer text-muted-foreground"
                            >
                              -- None --
                            </CommandItem>
                            {searchResults.map((item) => (
                              <CommandItem
                                key={item.id}
                                value={`item-${item.id}`}
                                onSelect={() => {
                                  field.onChange(item.id);
                                  setSelectedItemName(item.name);
                                  setItemSearchOpen(false);
                                  setItemSearchQuery('');
                                }}
                                className="cursor-pointer"
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{item.name}</span>
                                  {item.sku && (
                                    <span className="text-xs text-muted-foreground">
                                      Code: {item.sku}
                                    </span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    Link this service to a billable item for invoicing
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Brief description of this compliance service..."
                      className="resize-none"
                      rows={2}
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="portal_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Portal URL</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      placeholder="https://www.gst.gov.in/"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormDescription>
                    Official government portal for this service
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Scheduling Section */}
            <Separator />
            <div className="space-y-1">
              <h4 className="text-sm font-medium">Scheduling</h4>
              <p className="text-xs text-muted-foreground">
                Default schedule for tasks generated from this service
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Frequency</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                      value={field.value || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Not Set</SelectItem>
                        {RETURN_FREQUENCIES.map((freq) => (
                          <SelectItem key={freq.value} value={freq.value}>
                            {freq.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="due_day_of_month"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Day of Month</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        placeholder="e.g., 20"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="due_days_after_period"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Due Days After Period</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        placeholder="e.g., 30"
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : null)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="period_format"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Period Format</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., YYYY-MM, YYYY-Q"
                        {...field}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="applicable_months"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Applicable Months</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., 1, 4, 7, 10 (for quarterly)"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormDescription>
                    Comma-separated month numbers (1-12). Leave empty for all months.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Task Generation Config */}
            <Separator />
            <div className="space-y-1">
              <h4 className="text-sm font-medium">Task Generation</h4>
              <p className="text-xs text-muted-foreground">
                How tasks are auto-generated from this service
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="generate_days_before_due"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Generate Days Before Due</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min="0"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="default_priority"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Default Priority</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select priority" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TASK_PRIORITIES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="default_team_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Team</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === 'none' ? null : parseInt(value))
                    }
                    value={field.value?.toString() || 'none'}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select team" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No team</SelectItem>
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id.toString()}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Subscriptions to this service inherit this team. Generated tasks land in the team's pool.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="task_title_template"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Task Title Template</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., {service_name} - {period_id}"
                      {...field}
                      value={field.value || ''}
                    />
                  </FormControl>
                  <FormDescription>
                    Template for auto-generated task titles. Use {'{service_name}'}, {'{period_id}'}, {'{party_name}'}.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="auto_assign_to_subscription_user"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Auto-assign to Subscription User</FormLabel>
                    <FormDescription>
                      Assign generated tasks to the user on the subscription
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Enable this service for use in subscriptions
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {mode === 'create' ? 'Creating...' : 'Saving...'}
                  </>
                ) : mode === 'create' ? (
                  'Create Service'
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ComplianceServiceForm;
